import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, ChevronDown, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { emptySeller } from "@/components/PartyRows";
import { SendLinks } from "@/components/SendLinks";
import { usePostalCity, formatCp } from "@/lib/postal";
import { OwnerFields, ownerPayload, ownerError, Pills, OWNER_KINDS } from "@/components/OwnerFields";
import { PropertyFields, propertyPayload } from "@/components/PropertyFields";
import { EuroInput } from "@/components/EuroInput";
import { fmtCurrency } from "@/lib/format";

const num = (v) => (v === "" || v == null ? null : Number(v));
const OWNER = { venda: "Proprietário", arrendamento: "Senhorio", trespasse: "Trespassante" };

export default function NovoProcesso() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ property_ref: "", property_address: "", postal_code: "", locality: "", consultant_id: user.id });
  const [mediation, setMediation] = useState({ negocio: "venda", pay_cpcv_pct: 50, pay_deed_pct: 50, commission_type: "pct" });
  const [sellers, setSellers] = useState([emptySeller()]);
  const [owner, setOwner] = useState({ kind: "particular" });
  const [kind, setKind] = useState({ kind: null });
  const [wa, setWa] = useState(true);
  const [created, setCreated] = useState(null);
  const [dup, setDup] = useState(null);
  const pickConsultant = user.role !== "consultor";

  useEffect(() => {
    if (pickConsultant) api.get("/users").then(({ data }) => setUsers(data));
    api.get("/agency/cmi-settings").then(({ data }) => {
      if (data.default_commission_pct != null) setMediation((m) => (m.commission_value == null ? { ...m, commission_value: data.default_commission_pct } : m));
    }).catch(() => {});
  }, [pickConsultant]);
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  usePostalCity(f.postal_code, f.locality, (v) => set("locality", v));
  if (created) return <SendLinks c={created} onDone={() => navigate(`/processo/${created.id}?nova=1`)} />;

  const submit = async (e, allowDup = false) => {
    e?.preventDefault();
    if (!f.property_address.trim()) { setErr("Indique a morada do imóvel."); return; }
    if (f.postal_code.trim() && !/^\d{4}-\d{3}$/.test(f.postal_code.trim())) { setErr("Código postal no formato 0000-000."); return; }
    const place = `${f.postal_code.trim()} ${f.locality.trim()}`.trim();
    const address = [f.property_address.trim(), place].filter(Boolean).join(" · ");
    if (kind.kind === "terreno" && !kind.land) { setErr("Indique se o terreno é rústico ou urbano."); return; }
    const oe = ownerError(owner, sellers, true);
    if (oe) { setErr(oe); return; }
    setErr(""); setDup(null); setSaving(true);
    try {
      const { data } = await api.post("/angariacoes", {
        property_ref: f.property_ref, property_address: address, consultant_id: f.consultant_id,
        property_type: null, ...ownerPayload(owner, sellers), ...propertyPayload(kind),
        mediation: { ...mediation, commission_type: mediation.commission_type || "pct", asking_price: num(mediation.asking_price), commission_value: num(mediation.commission_value), duration_months: num(mediation.duration_months), pay_cpcv_pct: num(mediation.pay_cpcv_pct), pay_deed_pct: num(mediation.pay_deed_pct),
          share_agency: (mediation.share_agency || "").trim() || null, share_pct: (mediation.share_agency || "").trim() ? num(mediation.share_pct) : null },
        contract_file_id: null, allow_duplicate: allowDup,
      });
      toast.success(`Angariação ${data.ref} criada`);
      const links = (data.links || []).filter((l) => l.role === "vendedor");
      if (wa && links.length) { setCreated({ id: data.id, ref: data.ref, links }); setSaving(false); return; }
      navigate(`/processo/${data.id}?nova=1`);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      if (d?.code === "duplicate") setDup(d); else setErr(apiErr(d));
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="np-page">
      <button onClick={() => navigate("/processos")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-ec-green transition-colors" data-testid="np-back">
        <ArrowLeft size={16} /> Processos
      </button>
      <h1 className="font-heading text-2xl md:text-3xl text-ec-green" data-testid="np-title">Nova angariação</h1>

      <form onSubmit={submit} className="space-y-5">
        <div><Label>Morada</Label><Input value={f.property_address} onChange={(e) => set("property_address", e.target.value)} className="mt-1.5 bg-white" placeholder="Rua …, n.º …, andar" data-testid="np-address" /></div>
        <PropertyFields k={kind} setK={setKind} />
        <div className="space-y-2">
          <Label>{OWNER[mediation.negocio] || "Proprietário"}</Label>
          <OwnerFields o={owner} setO={setOwner} sellers={sellers} setSellers={setSellers} hideKind />
        </div>
        <PriceFields m={mediation} setM={setMediation} />
        {pickConsultant && <ConsultantPick users={users} me={user} value={f.consultant_id} onChange={(v) => set("consultant_id", v)} />}
        <MoreDetails f={f} set={set} m={mediation} setM={setMediation} owner={owner} setOwner={setOwner} />

        {dup && <DuplicateWarning d={dup} onOpen={() => navigate(`/processo/${dup.id}`)} onCreate={() => submit(null, true)} />}
        {err && <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2" data-testid="np-error">{err}</div>}

        <label className="flex items-center gap-3 rounded-[12px] border border-slate-100 bg-white px-3.5 py-3 text-sm text-slate-800 cursor-pointer">
          <input type="checkbox" checked={wa} onChange={(e) => setWa(e.target.checked)} className="w-4 h-4 accent-emerald-600" data-testid="np-send-whatsapp" />
          Enviar o link por WhatsApp ao proprietário
        </label>
        <button type="submit" disabled={saving} data-testid="np-submit" style={{ backgroundColor: "var(--ac)" }}
          className="w-full inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
          {saving && <Loader2 size={15} className="animate-spin" />} Criar angariação
        </button>
      </form>
    </div>
  );
}

// Só consultores ativos, sem repetidos; quem cria aparece sempre (é o valor por defeito).
function ConsultantPick({ users, me, value, onChange }) {
  const seen = new Set();
  const list = [{ id: me.id, name: me.name }, ...users.filter((u) => u.role === "consultor" && (u.status || "ativo") === "ativo")]
    .filter((u) => !seen.has(u.id) && seen.add(u.id));
  return (
    <div className="max-w-xs">
      <Label>Consultor responsável</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5 bg-white" data-testid="np-consultant"><SelectValue /></SelectTrigger>
        <SelectContent>{list.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`np-consultant-${u.id}`}>{u.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function PriceFields({ m, setM }) {
  const up = (k, v) => setM({ ...m, [k]: v });
  const fixed = m.commission_type === "valor";
  return (
    <div className="space-y-2" data-testid="np-mediation">
      <div className="grid grid-cols-[1fr_7rem] gap-3">
        <div><Label>Preço (€)</Label><EuroInput value={m.asking_price ?? ""} onChange={(v) => up("asking_price", v)} className="mt-1.5 bg-white" data-testid="np-asking" /></div>
        <div><Label>Comissão {fixed ? "(€)" : "(%)"}</Label>{fixed
          ? <EuroInput decimals value={m.commission_value ?? ""} onChange={(v) => up("commission_value", v)} className="mt-1.5 bg-white" data-testid="np-commission" />
          : <Input type="number" step="0.5" inputMode="decimal" value={m.commission_value ?? ""} onChange={(e) => up("commission_value", e.target.value)} className="mt-1.5 bg-white" data-testid="np-commission" />}</div>
      </div>
      <MediationSummary m={m} />
    </div>
  );
}

function MoreDetails({ f, set, m, setM, owner, setOwner }) {
  const [open, setOpen] = useState(false);
  const up = (k, v) => setM({ ...m, [k]: v });
  const pct = (v) => (v === "" ? "" : Math.min(100, Math.max(0, Number(v))));
  const split = (k, other) => (v) => { const x = pct(v); setM({ ...m, [k]: x, [other]: x === "" ? "" : 100 - x }); };
  return (
    <div className="rounded-[12px] border border-slate-100 bg-white" data-testid="np-more">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between gap-3 px-3.5 py-3 text-left" data-testid="np-more-toggle">
        <span className="min-w-0"><span className="block text-[15px] font-semibold text-slate-900">Mais detalhes</span>
          <span className="block text-[13px] text-slate-500 truncate">Código postal, referência SOU KW, exclusividade…</span></span>
        <ChevronDown size={16} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3.5 pb-4 space-y-4" data-testid="np-more-body">
          <div className="grid grid-cols-[8rem_1fr] gap-3">
            <div><Label>Código postal</Label><Input value={f.postal_code} onChange={(e) => set("postal_code", formatCp(e.target.value))} className="mt-1.5" placeholder="0000-000" inputMode="numeric" data-testid="np-postal-code" /></div>
            <div><Label>Cidade</Label><Input value={f.locality} onChange={(e) => set("locality", e.target.value)} className="mt-1.5" placeholder="ex.: Lisboa" data-testid="np-locality" /></div>
          </div>
          <div><Label>Referência (SOU KW / CRM)</Label><Input value={f.property_ref} onChange={(e) => set("property_ref", e.target.value)} className="mt-1.5" placeholder="Opcional" data-testid="np-ref" /></div>
          <div><Label>Tipo de proprietário</Label><div className="mt-1.5"><Pills options={OWNER_KINDS} value={owner.kind} onChange={(v) => setOwner({ ...owner, kind: v })} prefix="np-owner-kind" /></div></div>
          <div><Label>Negócio</Label><div className="mt-1.5"><Pills options={[["venda", "Venda"], ["arrendamento", "Arrendamento"], ["trespasse", "Trespasse"]]} value={m.negocio} onChange={(v) => up("negocio", v)} prefix="np-negocio" /></div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>% paga no CPCV</Label><Input type="number" inputMode="numeric" min="0" max="100" value={m.pay_cpcv_pct ?? ""} onChange={(e) => split("pay_cpcv_pct", "pay_deed_pct")(e.target.value)} className="mt-1.5" data-testid="np-pay-cpcv" /></div>
            <div><Label>% na escritura</Label><Input type="number" inputMode="numeric" min="0" max="100" value={m.pay_deed_pct ?? ""} onChange={(e) => split("pay_deed_pct", "pay_cpcv_pct")(e.target.value)} className="mt-1.5" data-testid="np-pay-deed" /></div>
          </div>
          <div className="flex items-center gap-3"><Label>Exclusividade</Label><Switch checked={!!m.exclusive} onCheckedChange={(v) => up("exclusive", v)} data-testid="np-exclusive" /></div>
          <div className="grid grid-cols-[1fr_6rem] gap-3">
            <div><Label>Partilha de comissão com</Label><Input value={m.share_agency || ""} onChange={(e) => up("share_agency", e.target.value)} className="mt-1.5" placeholder="Agência (opcional)" data-testid="np-share-agency" /></div>
            <div><Label>% para ela</Label><Input type="number" inputMode="numeric" min="0" max="100" value={m.share_pct ?? ""} disabled={!(m.share_agency || "").trim()} onChange={(e) => up("share_pct", pct(e.target.value))} className="mt-1.5" data-testid="np-share-pct" /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function DuplicateWarning({ d, onOpen, onCreate }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 space-y-3" data-testid="np-duplicate">
      <p className="text-sm text-amber-900">
        Já tens esta angariação: <strong>{d.ref}</strong> · {d.address}, com o mesmo proprietário.
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onOpen} data-testid="np-duplicate-open"
          className="bg-ec-green text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-ec-greenDark transition-colors">Abrir {d.ref}</button>
        <button type="button" onClick={onCreate} data-testid="np-duplicate-create"
          className="px-4 py-2 rounded-lg text-sm text-slate-600 border border-slate-200 hover:bg-white transition-colors">Criar outra na mesma</button>
      </div>
    </div>
  );
}

function MediationSummary({ m }) {
  const price = Number(m.asking_price) || 0;
  if (!price) return null;
  const fixed = m.commission_type === "valor";
  const c = fixed ? Number(m.commission_value) || 0 : (price * (Number(m.commission_value) || 0)) / 100;
  const part = (pct) => (pct === "" || pct == null ? null : (c * Number(pct)) / 100);
  const cpcv = part(m.pay_cpcv_pct), deed = part(m.pay_deed_pct);
  const txt = [fmtCurrency(price), c ? `comissão ${fmtCurrency(c)} + IVA` : null,
    c && cpcv != null ? `${fmtCurrency(cpcv)} no CPCV` : null, c && deed != null ? `${fmtCurrency(deed)} na escritura` : null].filter(Boolean).join(" · ");
  return <p className="text-sm text-slate-600 tabular-nums" data-testid="np-mediation-summary">{txt}</p>;
}
